/**
 * Which dealership the signed-in user is currently looking at.
 *
 * Most people have exactly one and never think about this. SAG staff span
 * several, so for them the app has to answer three questions at all times:
 * which dealership's data is on screen, how to move to another one, and whether
 * they may change anything here.
 *
 * All three come from one call. `GET /api/workspaces` returns the dealerships
 * the caller may look at:
 *
 *     [{ slug, name, status, read_only }]
 *
 * A dealer user gets exactly one entry with `read_only: false`; SAG staff get
 * several, all `read_only: true`. Picking one parks its slug in sessionStorage,
 * where the API client picks it up and sends it as `X-Tenant-Slug` on every
 * request — that header is a *preference*, never an authorisation. The backend
 * validates it against the caller's own memberships and rejects anything else,
 * and it is what enforces read-only. Hiding the write actions here is about the
 * app being legible, not about access control.
 *
 * Nothing is kept beyond the signed-in session: the provider is mounted only
 * inside the authenticated branch of the app, so signing out unmounts it and
 * takes the fetched list with it, while `clearSessionScopedState()` clears the
 * stored slug. A tab handed to the next person starts blank.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import apiService from '../services/apiService';
import { WORKSPACE_SLUG_KEY } from '../config/sessionKeys';
import SelectWorkspaceDialog from '../components/Workspace/SelectWorkspaceDialog';
import WorkspaceNotice from '../components/Workspace/WorkspaceNotice';

const WorkspaceContext = createContext(null);

// What the app looks like with no provider above it — and equally what it looks
// like when /api/workspaces cannot be reached: one dealership, no choice to
// offer, nothing hidden. That is exactly how the app behaved before this
// existed, which is the right thing to fall back to.
const NO_CHOICE = {
    workspaces: [],
    activeSlug: null,
    activeWorkspace: null,
    readOnly: false,
    canSwitch: false,
    requestWorkspace: async () => ({ slug: null }),
};

const readStoredSlug = () => {
    // Storage can be unavailable (private mode, blocked cookies).
    try { return sessionStorage.getItem(WORKSPACE_SLUG_KEY); } catch (e) { return null; }
};

const writeStoredSlug = (slug) => {
    try { sessionStorage.setItem(WORKSPACE_SLUG_KEY, slug); } catch (e) { /* nothing to store into */ }
};

export function useWorkspace() {
    return useContext(WorkspaceContext) || NO_CHOICE;
}

export function WorkspaceProvider({ children }) {
    const [workspaces, setWorkspaces] = useState([]);
    const [activeSlug, setActiveSlug] = useState(readStoredSlug);
    // Held while the dialog is open; `resolve` belongs to the promise the caller
    // of requestWorkspace() is waiting on.
    const [pending, setPending] = useState(null);
    // Set only when a fetch the user themselves asked for could not be made. The
    // one at sign-in is nobody's request and is not worth a message.
    const [askFailed, setAskFailed] = useState(false);
    // Whatever had focus when the picker opened, for when the user backs out (QA A-4).
    const openerRef = useRef(null);

    // The answer, or the attempt to get one, shared by whoever asks first: the
    // header renders off it and the picker awaits the same promise, so clicking
    // during a slow fetch joins it rather than starting another.
    //
    // Only a *successful* response is kept. "Asked, and this caller has one
    // dealership" and "could not ask" are different facts, and caching the
    // second as the first is what silently removes the feature for a whole
    // session: the first fetch happens at sign-in, exactly when a scaled-to-zero
    // backend is still waking up, and one cold start would then pin the caller
    // to an empty list until they thought to reload the page.
    const loadRef = useRef(null);
    const load = useCallback(() => {
        if (!loadRef.current) {
            // Through Promise.resolve so that a client that cannot even make the
            // call fails the same way as one whose call was refused.
            const attempt = Promise.resolve()
                .then(() => apiService.getWorkspaces())
                .then((list) => {
                    const answer = Array.isArray(list) ? list : [];
                    // Publish here rather than at the call site: an answer that
                    // arrives on a re-attempt has to reach the header and the
                    // switcher too, not just whoever happened to ask for it.
                    setWorkspaces(answer);
                    return { asked: true, list: answer };
                })
                .catch(() => {
                    // Not an answer — forget it, so the next ask asks again.
                    // Guarded on identity so a newer attempt is never dropped.
                    if (loadRef.current === attempt) loadRef.current = null;
                    return { asked: false, list: [] };
                });
            loadRef.current = attempt;
        }
        return loadRef.current;
    }, []);

    // Ask once at sign-in so the header can name the dealership without waiting
    // for anyone to click. `load` publishes the result itself.
    useEffect(() => { load(); }, [load]);

    const choose = useCallback((slug) => {
        writeStoredSlug(slug);
        setActiveSlug(slug);
    }, []);

    /**
     * Settle on a dealership to work in.
     *
     * Resolves to `{ slug }` once one is in force, or `null` if the user backed
     * out. With a single dealership — or none we could fetch — it settles
     * without asking: a dialog whose answer is foregone is just a click.
     *
     * A click is an explicit request, so it re-attempts a fetch that failed
     * earlier rather than serving the emptiness that failure left behind. If
     * that attempt fails too the caller is told and still goes through to the
     * dashboard — a dead button is worse than an unswitched one.
     */
    const requestWorkspace = useCallback(async () => {
        setAskFailed(false);
        const { asked, list } = await load();
        if (!asked) setAskFailed(true);
        if (list.length === 0) return { slug: readStoredSlug() };
        if (list.length === 1) {
            choose(list[0].slug);
            return { slug: list[0].slug };
        }
        openerRef.current = typeof document !== 'undefined' ? document.activeElement : null;
        return new Promise((resolve) => setPending({ resolve }));
    }, [load, choose]);

    const handlePick = useCallback((workspace) => {
        choose(workspace.slug);
        setPending((current) => {
            current?.resolve({ slug: workspace.slug });
            return null;
        });
    }, [choose]);

    const handleCancel = useCallback(() => {
        setPending((current) => {
            current?.resolve(null);
            return null;
        });
        // Back to the control that asked (the picker took focus to its first item).
        const opener = openerRef.current;
        openerRef.current = null;
        if (opener && opener !== document.body && typeof opener.focus === 'function') {
            setTimeout(() => { if (document.contains(opener)) opener.focus(); }, 0);
        }
    }, []);

    const value = useMemo(() => {
        // Someone who never picked still has an active dealership if there is
        // only one to have — that is the ordinary dealer user, and the read-only
        // flag has to reach the UI for them too.
        const activeWorkspace = workspaces.find((w) => w.slug === activeSlug)
            || (workspaces.length === 1 ? workspaces[0] : null);
        return {
            workspaces,
            activeSlug,
            activeWorkspace,
            readOnly: Boolean(activeWorkspace?.read_only),
            canSwitch: workspaces.length > 1,
            requestWorkspace,
        };
    }, [workspaces, activeSlug, requestWorkspace]);

    return (
        <WorkspaceContext.Provider value={value}>
            {children}
            {askFailed && (
                <WorkspaceNotice onDismiss={() => setAskFailed(false)}>
                    Could not load your dealership list just now — showing the one you had open.
                    Try again in a moment.
                </WorkspaceNotice>
            )}
            {pending && (
                <SelectWorkspaceDialog
                    workspaces={workspaces}
                    activeSlug={activeSlug}
                    onPick={handlePick}
                    onCancel={handleCancel}
                />
            )}
        </WorkspaceContext.Provider>
    );
}

WorkspaceProvider.propTypes = {
    children: PropTypes.node,
};

export default WorkspaceContext;
